import * as React from 'react';
import { I18nContextInterface } from '@om-fe/core/core/i18n/utils';
import withTranslation from '@om-fe/core/core/i18n/with-translation';
import { Button } from '@om-fe/widgets/dist/components/button/button';
import {
    DEFAULT_DISPLAYED_NUMBER_OF_MATCHES,
    MATCH_LIST_BEM as bem,
    MATCH_LIST_GROUP_BY,
    MATCH_LIST_GROUP_BY_COUNTRY,
    MATCH_LIST_GROUP_BY_CATEGORY,
    MatchListItemLayout,
} from './utils/match-list.constants';
import Anchor from '@om-fe/core/components/anchor/anchor';
import { ParamModel, RouteNames } from '@om-fe/core/core/routing/utils';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { FilteredMarketGroupsOverview, FilteredMatchesWithEvents, FilteredStreamingProvider, MatchListCommon } from './utils/match-list-models';
import { DEFAULT_MARKETS_HEADER_OPTIONS } from './utils/markets-header.utils';

import { LiveMatchListStoreModel } from './containers/live-match-list/live-match-list.constants';
import MatchListGroup from '../match-list-group/match-list-group';
import MatchListMixedMarketsHeader from './markets-headers/match-list-mixed-markets-header';
import MarketsHeader from './markets-headers/markets-header';
import getSlice from '@om-fe/core/utils/get-slice';
import { UpcomingMatchListStoreModel } from './containers/upcoming-match-list/upcoming-match-list.constants';
import { NextMatchListStoreModel } from './containers/next-match-list/next-match-list.constants';
import { FavoriteMatchListDisciplineItemStoreModel } from './containers/favorite-match-list/favorite-match-list.constants';
import { DisciplineLiveStatus, VirtualSportsDisciplines } from '@om-fe/models';
import MatchListSkeleton from './match-list-skeleton';
import { PlatformTypes, SendMessageType, isBrowser, sendMessageWithoutWarning, listenToMessageAll, ReceivedMessageType } from '@om-fe/utils';
import { isEsportsDiscipline } from '@om-fe/core/components/sport-parent/sport-parent.utils'
import { setVirtualStateExtraParams, getCurrentVirtualState, VirtualStateModel, setVirtualStateAction, setVirtualStateFocusVisible, VirtualStateVisibleType, BannerMatrixConfig, getVirtualStateExtraParams } from '@om-fe/core/core/routing/custom-routes/custom-routes.utils';
import { scrollContainerToPosition, SCROLL_CONTAINER_TOP_CLASS } from '@om-fe/core/utils/dom';
import getConfig, { MarketsHeaderOptions, OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { BonusType } from '../match-list-group/match-list-special-header';
import values from 'lodash/values';
import { StreamProviders } from '@om-fe/widgets/dist/components/video-stream/stream.constants';

interface MatchListDefaultProps {
    readonly limitNumberOfEvents: number | undefined;
    readonly marketsHeaderOptions: MarketsHeaderOptions;
    readonly showMarketsName: boolean;
    readonly showIsEmpty: boolean;
    readonly itemLayout: MatchListItemLayout;
    readonly showMatchListHeader: boolean;
    readonly showMarketInMatchListGroupHeader: boolean;
}

export interface MatchListAddMoreAnchor {
    readonly text: string;
    readonly routeName: string;
    readonly routeParams: ParamModel;
}

type MatchListSliceStoreModel = UpcomingMatchListStoreModel
    | LiveMatchListStoreModel
    | NextMatchListStoreModel
    | FavoriteMatchListDisciplineItemStoreModel;

export type MatchListPublicProps = Partial<MatchListDefaultProps> & MatchListCommon & {
    readonly addMore?: (instanceId: string, limitNumberOfEvents: number) => void;
    readonly addMoreAnchor?: MatchListAddMoreAnchor;
    readonly instanceId: string;
    readonly disciplineId?: string;
    readonly endInterval?: number;
    readonly isDefault?: boolean;
    // TODO Check if this prop is needed on the MatchListPublicProps interface and also on MatchListStoreProps interface
    readonly isLoadingData?: boolean | undefined;
    readonly liveStatus: DisciplineLiveStatus;
    readonly limit?: number;
    readonly locationId?: string;
    readonly eventCategoryId?: string;
    readonly mainMarketsLimit?: number;
    readonly startInterval?: number;
    readonly sliceKey: string;
    readonly totalRecords?: number;
    readonly tournamentId?: string;
    readonly isAuthenticated?: boolean;
    readonly groupOverview?: string;
    readonly selectGroupsOverview?: (item: string) => void;
    readonly filteredMarketGroupsOverviewList?: string[];
    readonly filteredMarketGroupsOverview?: Record<string, FilteredMarketGroupsOverview>;
    readonly isHasDrawer?: boolean;
    readonly deviceType?: PlatformTypes;
    readonly preventRedirect?: boolean;
    readonly bonusType?: BonusType;
    readonly isVirtual?: boolean;
    readonly extraParam?: string;
};

interface MatchListStoreProps {
    readonly disciplineName: string | undefined;
    readonly filteredMainMarketsList: string[];
    readonly filteredMainOutrightsMarketsList: string[];
    readonly filteredMatchesIds: string[];
    readonly filteredMatchesWithEvents?: Record<string, FilteredMatchesWithEvents>;
    readonly isLoadingData: boolean;
    readonly isLoadingMoreData: boolean;
    readonly showEventCategory?: boolean;
    readonly page?: RouteNames;
    readonly virtualRenderMatchList?: boolean;
}

type MatchListProps = MatchListPublicProps
    & MatchListDefaultProps
    & MatchListStoreProps
    & I18nContextInterface;

class MatchListComponent extends React.Component<MatchListProps> {
    timeoutId: NodeJS.Timeout | undefined = undefined;
    state = {
        activeStatistic: '',
        waitVirtualStateParent: false,
    }

    static defaultProps: MatchListDefaultProps = {
        limitNumberOfEvents: DEFAULT_DISPLAYED_NUMBER_OF_MATCHES,
        marketsHeaderOptions: DEFAULT_MARKETS_HEADER_OPTIONS,
        showMarketsName: true,
        showIsEmpty: true,
        itemLayout: MatchListItemLayout.V2,
        showMatchListHeader: true,
        showMarketInMatchListGroupHeader: false,
    };

    constructor(props: MatchListProps) {
        super(props);
        const { page } = props;
        if (page !== RouteNames.home) {
            setVirtualStateAction(false);
        }
        this.handleScroll();
    }

    virtualStateListenerUnsubscribe: (() => void) | null = null;

    componentDidMount() {
        const config: OMFE2ConfigModel = getConfig();
        if (isBrowser() && config.virtualState) {
            this.virtualStateListenerUnsubscribe = listenToMessageAll(ReceivedMessageType.VirtualStateTrigger, () => {
                const storage: MatchListProps = getVirtualStateExtraParams<MatchListProps>();
                const { instanceId, addMore } = this.props;

                this.setState({ waitVirtualStateParent: true }, () => {
                    setTimeout(() => {
                        this.setState({ waitVirtualStateParent: false });
                    }, 0);
                });
                if (addMore && storage?.matchLimit) {
                    addMore(instanceId, storage?.matchLimit);
                }
            });
        }
    }

    handleScrollCallback = () => {
        clearTimeout(this.timeoutId);
        this.timeoutId = setTimeout(() => {
            setVirtualStateFocusVisible(`.${SCROLL_CONTAINER_TOP_CLASS} div.EventItem`, `a.Anchor.Details`, VirtualStateVisibleType.EVENT);
        }, 500);
    };

    componentWillUnmount() {
        const config: OMFE2ConfigModel = getConfig();
        if (isBrowser() && config.virtualState) {
            const scrollableElementList: NodeListOf<Element> = document.querySelectorAll(`.${SCROLL_CONTAINER_TOP_CLASS}`);
            scrollableElementList.forEach((element) => {
                element.removeEventListener('scroll', this.handleScrollCallback);
            });

            if (this.virtualStateListenerUnsubscribe) {
                this.virtualStateListenerUnsubscribe();
            }
        }
    }

    handleScroll() {
        const config: OMFE2ConfigModel = getConfig();
        if (isBrowser() && config.virtualState) {
            const scrollableElementList: NodeListOf<Element> = document.querySelectorAll(`.${SCROLL_CONTAINER_TOP_CLASS}`);
            scrollableElementList.forEach((element) => {
                element.addEventListener('scroll', this.handleScrollCallback);
            });
        }
    }

    getLoadMoreNode(): React.ReactNode {
        const { isLoadingData, limit, limitNumberOfEvents, totalRecords, addMoreAnchor, addMore, gettext } = this.props;

        if (isLoadingData || limitNumberOfEvents === undefined || totalRecords === undefined || (limit && limit >= totalRecords)) {
            return null;
        }

        if (addMoreAnchor && addMore) {
            // eslint-disable-next-line no-console
            console.error('You should not use both `redirectAnchor` and `addMore` props on `MatchList`');
        }

        if (addMoreAnchor) {
            return (
                <Anchor
                    routeName={addMoreAnchor.routeName}
                    routeParams={addMoreAnchor.routeParams}
                >
                    {addMoreAnchor.text}
                </Anchor>
            );
        } else {
            return (
                <Button
                    onClick={this.handleAddMoreMatches}
                    themeColor="ghost"
                    className={bem('MainButton')}
                >
                    {gettext('Show more')}
                </Button>
            );
        }
    }

    getGroupedMatchesOrder(group: string): FilteredMatchesWithEvents[][] {
        const { filteredMatchesIds, filteredMatchesWithEvents } = this.props;

        const mutableIndexMap: Record<string, number> = {};
        const mutableGroupedMatches: FilteredMatchesWithEvents[][] = [];

        if (!filteredMatchesWithEvents) {
            return mutableGroupedMatches;
        }

        filteredMatchesIds.forEach((id: string): void => {
            const item: FilteredMatchesWithEvents = filteredMatchesWithEvents[id];
            const key: string = item[group];
            const index: number | undefined = mutableIndexMap[key];

            if (index !== undefined) {
                mutableGroupedMatches[index].push(item);
            } else {
                mutableGroupedMatches.push([item]);
                mutableIndexMap[key] = mutableGroupedMatches.length - 1;
            }
        });

        return mutableGroupedMatches;
    }

    setActiveStatistic = (instanceId: string): void => {
        const { activeStatistic } = this.state;

        if (instanceId === activeStatistic) {
            this.setState({
                activeStatistic: '',
            });
            
            return;
        }

        this.setState({
            activeStatistic: instanceId,
        });
    };

    getGroupedMatchesNode(groupType: string): React.ReactNode {
        const {
            activeMatchId,
            eventItemExtraClassname,
            numberOfGroupsExpanded,
            locationId,
            eventCategoryId,
            marketsHeaderOptions,
            numberOfOddsExpanded,
            sliceKey,
            routeName,
            liveStatus,
            itemLayout,
            filteredMainMarketsList,
            filteredMainOutrightsMarketsList,
            showMarketInMatchListGroupHeader,
            mainMarketsLimit,
            filteredMarketGroupsOverviewList,
            filteredMarketGroupsOverview,
            groupOverview,
            isHasDrawer,
            deviceType,
            selectGroupsOverview,
            showEventCategory,
            preventRedirect,
            filteredMatchesWithEvents,
            bonusType,
            isVirtual,
            instanceId,
        } = this.props;
        const { activeStatistic } = this.state;
        const groupedMatchType: string = groupType === MATCH_LIST_GROUP_BY_COUNTRY && showEventCategory ? MATCH_LIST_GROUP_BY_CATEGORY : groupType;
        const groupedMatches: FilteredMatchesWithEvents[][] = itemLayout === MatchListItemLayout.Special ? [values(filteredMatchesWithEvents)] : this.getGroupedMatchesOrder(groupedMatchType);
        const mutableGroupedMatchesNode: React.ReactNode[] = [];
        let numberOfOddsExpandedRemaining: number | undefined = numberOfOddsExpanded;

        groupedMatches.forEach((matchGroup, groupIndex) => {
            mutableGroupedMatchesNode.push(
                <MatchListGroup
                    activeMatchId={activeMatchId}
                    eventItemExtraClassName={eventItemExtraClassname}
                    index={groupIndex}
                    key={`${matchGroup[0]}-${groupIndex}`}
                    items={matchGroup}
                    isExpanded={numberOfGroupsExpanded === undefined || numberOfGroupsExpanded > groupIndex}
                    groupData={matchGroup[0]}
                    locationId={locationId}
                    categoryId={eventCategoryId}
                    marketsHeaderOptions={marketsHeaderOptions}
                    numberOfOddsExpanded={numberOfOddsExpandedRemaining !== undefined ? numberOfOddsExpandedRemaining : undefined}
                    routeName={routeName}
                    sliceKey={sliceKey}
                    liveStatus={liveStatus}
                    itemLayout={itemLayout}
                    filteredMainMarketsList={filteredMainMarketsList}
                    filteredMainOutrightsMarketsList={filteredMainOutrightsMarketsList}
                    showMarketInMatchListGroupHeader={showMarketInMatchListGroupHeader}
                    mainMarketsLimit={mainMarketsLimit}
                    activeStatistic={activeStatistic}
                    setActiveStatistic={this.setActiveStatistic}
                    selectGroupsOverview={selectGroupsOverview}
                    groupOverview={groupOverview}
                    filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewList}
                    filteredMarketGroupsOverview={filteredMarketGroupsOverview}
                    isHasDrawer={isHasDrawer}
                    deviceType={deviceType}
                    showEventCategory={showEventCategory}
                    preventRedirect={preventRedirect}
                    bonusType={bonusType}
                    isVirtual={isVirtual}
                    instanceId={instanceId}
                />,
            );

            if (numberOfOddsExpandedRemaining !== undefined) {
                const newNumberOfOddsExpandedRemaining = numberOfOddsExpandedRemaining - matchGroup.length;
                numberOfOddsExpandedRemaining = newNumberOfOddsExpandedRemaining > 0 ? newNumberOfOddsExpandedRemaining : 0;
            }
        });

        return mutableGroupedMatchesNode;
    }

    getMarketsHeaderNode(): React.ReactNode {
        const {
            disciplineId,
            disciplineName,
            filteredMainMarketsList,
            showMixedMatchListHeader,
            showMarketsName,
            sliceKey,
            selectGroupsOverview,
            filteredMarketGroupsOverviewList,
            filteredMarketGroupsOverview,
            isHasDrawer,
            deviceType,
            groupOverview,
            mainMarketsLimit,
        } = this.props;

        return (showMixedMatchListHeader) ? (
            <MatchListMixedMarketsHeader
                disciplineId={disciplineId}
                disciplineName={disciplineName}
                filteredMainMarketsList={filteredMainMarketsList}
                showMarketsName={showMarketsName}
                sliceKey={sliceKey}
            />
        ) : (
            <MarketsHeader
                filteredMainMarketsList={filteredMainMarketsList}
                showMarketsName={showMarketsName}
                sliceKey={sliceKey}
                groupOverview={groupOverview}
                selectGroupsOverview={selectGroupsOverview}
                filteredMarketGroupsOverviewList={filteredMarketGroupsOverviewList}
                filteredMarketGroupsOverview={filteredMarketGroupsOverview}
                isHasDrawer={isHasDrawer}
                deviceType={deviceType}
                mainMarketsLimit={mainMarketsLimit}
            />
        );
    }

    getMatchesNode(): React.ReactNode {
        const { showMatchListHeader, marketsHeaderOptions, disciplineId } = this.props;
        const matchListGroupType = (
            marketsHeaderOptions &&
            marketsHeaderOptions === MarketsHeaderOptions.SHOW_COUNTRY &&
            !isEsportsDiscipline(disciplineId))
                ? MATCH_LIST_GROUP_BY_COUNTRY : MATCH_LIST_GROUP_BY;

        return (
            <>
                {showMatchListHeader && this.getMarketsHeaderNode()}
                {this.getGroupedMatchesNode(matchListGroupType)}
            </>
        );
    }

    handleAddMoreMatches = (): void => {
        const { instanceId, limit, limitNumberOfEvents, addMore } = this.props;

        if (!addMore || !limitNumberOfEvents) {
            return;
        }

        addMore(instanceId, limitNumberOfEvents);
        setVirtualStateExtraParams({ matchLimit: limit + limitNumberOfEvents });
    };

    render(): React.ReactNode {
        const {
            disciplineId,
            extraClassName,
            filteredMatchesIds,
            gettext,
            isLoadingData,
            isLoadingMoreData,
            liveStatus,
            showIsEmpty,
            filteredMainMarketsList,
            isAuthenticated,
            itemLayout,
            isVirtual,
            virtualRenderMatchList,
        } = this.props;

        if (!filteredMatchesIds || !filteredMatchesIds.length) {
            return showIsEmpty && (
                <div className="EmptyList">
                    {gettext('No matches currently available')}
                </div>
            );
        }

        const config: OMFE2ConfigModel = getConfig();
        const virtualState: VirtualStateModel = getCurrentVirtualState();
        const bannerMatrixConfig: BannerMatrixConfig | undefined = config.bannerMatrix;

        if (
            config.virtualState &&
            !this.state.waitVirtualStateParent &&
            !isLoadingMoreData &&
            !isLoadingData &&
            virtualState &&
            !virtualState.action &&
            virtualState.extraParams?.focusEventVisible &&
            virtualState.extraParams?.focusFromTop &&
            virtualState.forwardState &&
            (virtualState.forwardState.name === RouteNames.event || virtualState.forwardState.name === RouteNames.liveEvent || virtualState.forwardState.name === RouteNames.tournamentLocation || virtualState.forwardState.name === RouteNames.tournamentCategories)
        ) {
            setTimeout(() => {
                scrollContainerToPosition(`a[class*='Anchor Details'][href*='${virtualState.extraParams?.focusEventVisible}']`, virtualState.extraParams?.focusFromTop);
                setVirtualStateAction();
            }, 0);
        }

        if (!isLoadingData && bannerMatrixConfig?.showOnFooter && isBrowser()) {
            if (document.querySelector(`#BannerFooter`)?.classList.contains('BannerWidgetBannerHidden')) {
                document.querySelector(`#BannerFooter`)?.classList.remove('BannerWidgetBannerHidden');
                sendMessageWithoutWarning({
                    type: SendMessageType.SliderRefresh,
                    isInternal: true,
                });
            }
        }

        return (
            <div className={bem({
                element: '',
                modifiers: {
                    [`Sport-${disciplineId}`]: !!disciplineId,
                    [`Outcomes-${filteredMainMarketsList.length}`]: !!filteredMainMarketsList.length,
                    LiveEvents: liveStatus === DisciplineLiveStatus.LIVE,
                    UpcomingEvents: liveStatus === DisciplineLiveStatus.NOT_LIVE,
                    hasFavorites: !!isAuthenticated && liveStatus === DisciplineLiveStatus.LIVE,
                    Highlights: liveStatus === DisciplineLiveStatus.BOTH,
                    Default: itemLayout !== MatchListItemLayout.Special,
                    WithBonuses: itemLayout === MatchListItemLayout.Special,
                    VirtualTemplate: !!isVirtual,
                },
                extra: extraClassName,
            })}>
                {!isLoadingData
                    ? (
                        <>
                            {isVirtual ? 
                                virtualRenderMatchList
                                ? this.getMatchesNode() 
                                : <MatchListSkeleton />
                            : this.getMatchesNode()
                            }
                            {isLoadingMoreData ? <MatchListSkeleton /> : this.getLoadMoreNode()}
                        </>
                    )
                    : <MatchListSkeleton />
                }
            </div>
        );
    }
}

const MatchList = compose<React.ComponentType<MatchListPublicProps>>(
    withTranslation<MatchListPublicProps>(),
    connect<MatchListStoreProps>(
        (state: AppState, props: MatchListPublicProps): MatchListStoreProps => {
            const { sliceKey, disciplineId, isVirtual, extraParam } = props;
            const slice: MatchListSliceStoreModel | undefined = getSlice<MatchListSliceStoreModel>(state, sliceKey);

            if (!slice) {
                return {
                    disciplineName: undefined,
                    filteredMainMarketsList: [],
                    filteredMainOutrightsMarketsList: [],
                    filteredMatchesIds: [],
                    filteredMatchesWithEvents: {},
                    isLoadingData: true,
                    isLoadingMoreData: false,
                    showEventCategory: false,
                    page: state.routing.name,
                };
            }

            let virtualRenderMatchList: boolean = true;
            if (
                isVirtual &&
                Object.keys(slice?.filteredStreamingProviderEventRelation).length
            ) {
                const firstStream: FilteredStreamingProvider = slice?.filteredStreamingProviderEventRelation[Object.keys(slice?.filteredStreamingProviderEventRelation)[0]];
                if (
                    firstStream &&
                    firstStream.name === StreamProviders.SportradarUOF &&
                    firstStream.url?.includes('aitcloud') &&
                    !extraParam &&
                    (state.breakpoints.large || state.breakpoints.xl) &&
                    (
                        disciplineId === VirtualSportsDisciplines.VirtualFootball ||
                        disciplineId === VirtualSportsDisciplines.VirtualBasketball
                    )
                ) {
                    virtualRenderMatchList = false;
                }
            }

            return {
                disciplineName: slice.discipline && slice.discipline.name,
                filteredMainMarketsList: slice.filteredMainMarketsList,
                filteredMatchesWithEvents: slice.filteredMatchesWithEvents,
                filteredMainOutrightsMarketsList: slice.filteredMainOutrightsMarketsList,
                filteredMatchesIds: slice.filteredMatchesIds,
                isLoadingData: slice.isLoadingData,
                isLoadingMoreData: slice.isLoadingMoreData,
                showEventCategory: slice.discipline && slice.discipline.showEventCategory,
                page: state.routing.name,
                virtualRenderMatchList: isVirtual ? virtualRenderMatchList : undefined,
            };
        },
    ),
)(MatchListComponent);
export default MatchList;
